import _isEqual from 'lodash/isEqual';
import cloneDeep from 'lodash/cloneDeep';
import {useMainStore} from "../../state/store";
import {reactive} from "vue";

export default class Form {
    constructor (fields = [], validator = null, options = {}) {

        this.store = useMainStore();
        this.validator = validator;
        this.data = {};
        this.original = {};
        this.options = {};
        this.originalOptions = {};
        this.rules = {};
        this.errors = {};
        this.submitting = false;
        this.validators = {
            required (value) {
                return !!value;
            },
            regex (value, regex) {
                return (new RegExp(regex)).test(value);
            }
        }

        fields.forEach(({name, value = '', rules = [], translatable = false, accessor = null}) => {
            if (translatable) {
                value = this.hydrateTranslatableField(value, accessor);
            } else {
                if (accessor) {
                    value = accessor(value);
                }
            }

            this.data[name] = this._getValue(value);
            this.original[name] = this._getValue(value);
            if (rules.length) { this.rules[name] = rules; }
            this.errors[name] = undefined;

            Object.defineProperty(this, name, {
                get: () => this.data[name],
                set: newValue => this.data[name] = newValue,
            });
        });

        for (const [key, value] of Object.entries(options)) {
            try {
                this.checkForDuplicatedKeys(key);
                this.options[key] = this._getValue(value);
                this.originalOptions[key] = this._getValue(value);

                Object.defineProperty(this, key, {
                    get: () => this.options[key],
                    set: newValue => this.options[key] = newValue,
                })
            } catch (e) {
                console.error(e);
            }
        }

        // some forms lose reactivity since vue3 migration, this is a workaround, might be compat mode 2 related
        this.data = reactive(this.data);
    }

    hydrateTranslatableField (value, accessor) {
        const mainStore = useMainStore();
        let languages = mainStore.currentCompany.jobsite.active_languages;

        if (typeof value === 'object' && !Array.isArray(value)) {
            languages.forEach(lang => {
                if (value[lang] === undefined) {
                    value[lang] = accessor ? accessor('') : '';
                }
            });
        } else {
            let storedValue = value;

            value = {};

            languages.forEach(lang => {
                value[lang] = storedValue;
            });
        }

        return value;
    }

    submit (action) {

        this.submitting = true;

        // Return the promise, so we can chain promises in our application where necessary.
        return action(this.data)
            .catch(error => {
                this._handleError(error);
                throw error;
            })
            .then(result => {
                this.finalize();
                return result;
            })
            .finally(() => {
                this.submitting = false;
            });
    }

    isDifferent (newData) {
        for (let field in this.data) {
            if (JSON.stringify(newData[field]) !== JSON.stringify(this.data[field])) {
                return true;
            }
        }
        return false;
    }

    isDirty (field) {
        if (field) return ! _isEqual(this.data[field], this.original[field]);
        else return ! (_isEqual(this.data, this.original) && _isEqual(this.options, this.originalOptions));
    }

    isValid () {
        return this.isDirty() && this.containsValidData();
    }

    containsValidData () {
        return !Object.keys(this.rules).some(attr => {
            return this.rules[attr].some(rule => {
                let [validator, ...attributes] = rule.split(':');
                return !this.validators[validator](this.data[attr], attributes);
            })
        });
    }

    reset (field, value) {
        if (field) {
            this._resetField(field, value);
        } else {
            for (let field in this.data) {
                this._resetField(field, value);
            }
        }
    }

    _resetField (field, value) {
        this.data[field] = value == null ? this.original[field] : value;
        this.errors[field] = undefined;
    }

    finalize () {
        for (let field in this.data) {
            this.original[field] = this.data[field];
        }
    }

    deleteProperty (property) {
        delete this.data[property];
        delete this.original[property];
        delete this.rules[property];
        delete this.errors[property];
    }

    checkForDuplicatedKeys(key) {
        if (this.data.hasOwnProperty(key)) {
            this._throwDuplicateError(key);
        }
    }

    setErrors(errors) {
        this.errors = errors;
    }

    resetErrors() {
        const keys = Object.keys(this.errors)
        keys.forEach(key => {
            this.errors[key] = undefined
        })
    }

    _handleError (error) {
        if (error.hasOwnProperty('response') && error.response.status === 422 && error.response.data.errors) {
            Object.keys(this.errors).forEach(field => {
                this.errors[field] = error.response.data.errors[field] ||
                    error.response.data.errors['data.attributes.' + field] ||
                    undefined;
            });
        }
    }

    _throwDuplicateError(key) {
        throw `Form: Cannot have duplicate keys: ${key}`;
    }

    _getValue (value) {

        if (typeof value !== 'object' || value === null) {
            return value;
        }

        return cloneDeep(value);
    }
}
